import { useEffect, useMemo } from "react"
import "../style.scss"
import { generatePathUrl } from "../../../utils"
import { GenericError, GenericErrorProps } from "../GenericError"
import { CMSConfiguration } from "@jnext/commons"
import { useCMSStore } from "store/cmsStore"
import { AuthService } from "../../../service/AuthService"
import { useTranslation } from "react-i18next"

type ErrorType = "404" | "401" | "503" | "504";

interface ErrorPageComponentProps {
  errorType: ErrorType;
}

export const ErrorPageComponent = ({ errorType }: ErrorPageComponentProps) => {
  const { t, i18n } = useTranslation()

  const { pages } = useCMSStore()

  const config: CMSConfiguration | undefined = useMemo(() => {
    switch (errorType) {
      case "404":
        return pages?.notFound
      case "401":
        return pages?.unauthorized
      case "503":
        return (pages as any)?.maintenance
      case "504":
        return pages?.serverError
      default:
        return undefined
    }
  }, [pages, errorType])

  const fallbackImage = [{ src: generatePathUrl("assets/error/illustrazioneManutenzione.png") }]

  const getMessage = (key: string, defaultTitle: string) => {
    return i18n.exists(key) ? t(key) : defaultTitle
  }

  const data: GenericErrorProps = useMemo(() => {
    const enabled = config?.enabled
    const page = config?.page

    const image = enabled && page?.banner?.image ? page.banner.image : fallbackImage

    switch (errorType) {
      case "404":
        return {
          title: getMessage("404ErrorTitle", "Pagina non trovata"),
          text: getMessage("404ErrorText", "La pagina che stai cercando potrebbe essere stata rimossa o non è mai esistita."),
          image
        }
      case "401":
        return {
          title: getMessage("NO_AUTH_PERMISSION", "Accesso non autorizzato"),
          text: getMessage("NO_AUTH_PERMISSION_TEXT", ""),
          image
        }
      case "503":
        return {
          title: getMessage("503ErrorTitle", "Servizio non disponibile"),
          text: getMessage("503ErrorText", "Il servizio è temporaneamente non disponibile. Ti preghiamo di riprovare più tardi."),
          image
        }
      case "504":
        return {
          title: getMessage("statusErrorTitle", "Timeout del server"),
          text: getMessage("statusErrorText", "La richiesta ha impiegato troppo tempo per ricevere una risposta. Verifica la tua connessione o riprova più tardi."),
          image
        }
      default:
        return {
          title: getMessage("DEFAULT_ERROR_TITLE", "Errore imprevisto"),
          text: getMessage("DEFAULT_ERROR_TEXT", "Si è verificato un errore. Stiamo lavorando per risolverlo al più presto."),
          image
        }
    }
  }, [config, errorType, t, i18n])

  useEffect(() => {
    if (errorType === "504") {
      AuthService.logoutClean()
      localStorage.clear()
    }
  }, [errorType])

  return <GenericError title={data.title} text={data.text} image={data.image} />
}
